import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { Auth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithPopup, signOut, updateProfile } from '@angular/fire/auth';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../auth.service';
import { LanguageSelectorComponent } from '../../shared/components/language-selector/language-selector.component';
import { ToastService } from '../../shared/services/toast/toast.service';

@Component({
  selector: 'app-join',
  template: `
    <div class="flex items-center justify-center p-4 sm:p-8 w-full min-h-screen">
      <div class="card pb-6 pt-8 sm:pt-10 px-6 sm:px-10 max-w-[440px] w-full flex flex-col items-center">
        <!-- Workspace Logo -->
        <div class="flex justify-center mb-6">
          <div style="height: 56px; max-width: 240px; display: flex; align-items: center; justify-content: center;">
            <img
              [src]="logoUrl || 'assets/creatives/logo-w70.png'"
              (error)="onLogoError($event)"
              class="signin-logo"
              style="max-height: 100%; max-width: 100%; width: auto; height: auto; object-fit: contain;"
              [alt]="workspaceName + ' logo'"
            />
          </div>
        </div>

        @if (loading()) {
          <div class="flex flex-col items-center justify-center py-8">
            <mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
            <p class="text-sm text-gray-500 mt-4">{{ 'Global.Loading' | translate }}...</p>
          </div>
        } @else if (error()) {
          <div class="flex flex-col items-center justify-center text-center">
            <h3 class="text-lg font-bold text-gray-800 mb-2">{{ 'UserManagement.LinkExpiredTitle' | translate }}</h3>
            <p class="text-sm text-gray-500 mb-6">{{ 'UserManagement.LinkExpiredDesc' | translate }}</p>
            <button mat-flat-button btnType="action" routerLink="/login">{{ 'Sessions.Login.BackToSignIn' | translate }}</button>
          </div>
        } @else {
          <!-- Join Link details -->
          <h2 class="text-xl font-bold text-[#1D2433] mb-2 text-center">{{ 'UserManagement.JoinWorkspaceTitle' | translate: { name: workspaceName } }}</h2>

          <p class="text-sm text-gray-500 mb-6 text-center">
            @if (allowedDomain) {
              {{ 'UserManagement.JoinInstructionsWithDomain' | translate: { domain: allowedDomain } }}
            } @else {
              {{ 'UserManagement.JoinInstructions' | translate }}
            }
          </p>

          @if (currentUser()) {
            <!-- Logged in state -->
            <div class="w-full flex flex-col items-center">
              <div class="bg-gray-50 rounded-xl p-2 w-full mb-2 border border-gray-100 flex flex-col items-center text-center">
                <span class="text-xs text-gray-400 mb-1">{{ 'UserManagement.LoggedInAs' | translate }}</span>
                <span class="text-sm font-semibold text-gray-800 truncate max-w-full">{{ currentUser()?.email }}</span>
              </div>

              @if (allowedDomain && !isEmailDomainValid()) {
                <div class="bg-red-50 text-red-700 rounded-xl p-2 w-full mb-6 text-sm flex items-start gap-2 justify-center">
                  <div class="text-center" [innerHTML]="'UserManagement.DomainLockWarning' | translate: { domain: allowedDomain }"></div>
                </div>
                <button mat-flat-button btnType="action" class="w-full mb-2" (click)="signOut()">{{ 'UserManagement.SignOutAndChangeAccount' | translate }}</button>
              } @else {
                <button mat-flat-button btnType="action" class="w-full mb-2" [disabled]="joining()" (click)="acceptJoin()">
                  @if (joining()) {
                    <span class="flex items-center gap-2 justify-center">
                      <mat-spinner diameter="18" color="accent"></mat-spinner>
                      {{ 'UserManagement.JoiningInProgress' | translate }}
                    </span>
                  } @else {
                    {{ 'UserManagement.JoinWorkspace' | translate }}
                  }
                </button>
              }

              <a (click)="signOut()" mat-flat-button btnType="neutral" class="w-full">{{ 'UserManagement.UseAnotherAccount' | translate }}</a>
            </div>
          } @else {
            <!-- Logged out state -->
            <div class="w-full flex flex-col">
              @if (authMode() === 'login') {
                <form [formGroup]="loginForm" class="flex flex-col text-left w-full" (ngSubmit)="joinWithLogin()">
                  <div class="w-full mb-2">
                    <mat-label class="form-label mb-1">{{ 'Sessions.Login.EmailField' | translate }}</mat-label>
                    <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
                      <input matInput formControlName="email" type="email" autocomplete="username" [placeholder]="'Sessions.Login.PasswordPlaceholder' | translate" />
                    </mat-form-field>
                  </div>

                  <div class="w-full mb-2">
                    <mat-label class="form-label mb-1">{{ 'Sessions.Login.PasswordField' | translate }}</mat-label>
                    <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
                      <input
                        matInput
                        formControlName="password"
                        [type]="hidePassword() ? 'password' : 'text'"
                        autocomplete="current-password"
                        [placeholder]="'Sessions.Login.PasswordPlaceholder' | translate"
                      />
                      <button mat-icon-button matSuffix type="button" (click)="hidePassword.set(!hidePassword())">
                        <mat-icon>{{ hidePassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
                      </button>
                    </mat-form-field>
                  </div>

                  <div class="flex justify-end mb-3">
                    <a class="text-sm text-primary hover:text-primary-dark cursor-pointer transition-colors font-medium opacity-0 pointer-events-none">
                      <!-- Hidden forgot password link to keep the spacing identical, or just leave it out, but user wants exact match -->
                      {{ 'Sessions.Login.ForgotPassWord' | translate }}
                    </a>
                  </div>

                  <button class="w-full py-1 text-sm rounded-lg font-medium mb-1" type="submit" [disabled]="authLoading() || loginForm.invalid" mat-flat-button color="primary">
                    {{ 'Sessions.Login.Login' | translate }}
                  </button>

                  <div class="or-divider flex items-center gap-3 my-3">
                    <div class="flex-1 h-px bg-gray-200"></div>
                    <span class="text-[13px] text-gray-400 whitespace-nowrap">{{ 'Sessions.Login.OrContinueWith' | translate }}</span>
                    <div class="flex-1 h-px bg-gray-200"></div>
                  </div>

                  <div class="flex flex-col gap-2">
                    <button
                      type="button"
                      matButton="tonal"
                      class="provider-login w-full border border-gray-200 rounded-lg justify-center flex items-center h-[42px]"
                      [disabled]="authLoading()"
                      (click)="joinWithProvider('google')"
                    >
                      <img src="assets/images/google.svg" class="h-4 mr-2" />
                      <span>{{ 'Sessions.Login.LoginWithGoogle' | translate }}</span>
                    </button>

                    <button
                      type="button"
                      matButton="tonal"
                      class="provider-login w-full border border-gray-200 rounded-lg justify-center flex items-center h-[42px]"
                      [disabled]="authLoading()"
                      (click)="joinWithProvider('microsoft')"
                    >
                      <img src="assets/images/microsoft.svg" class="h-4 mr-2" />
                      <span>{{ 'Sessions.Login.LoginWithMicrosoft' | translate }}</span>
                    </button>
                  </div>

                  <div class="flex justify-center mt-4">
                    <span class="text-sm text-gray-500">{{ 'Sessions.Login.NoAccount' | translate }}&nbsp;</span>
                    <a (click)="authMode.set('signup')" class="text-sm font-semibold text-primary hover:text-primary-dark cursor-pointer transition-colors">
                      {{ 'Sessions.Login.SignUpLink' | translate }}
                    </a>
                  </div>
                </form>
              } @else {
                <form [formGroup]="signupForm" class="flex flex-col text-left w-full" (ngSubmit)="joinWithSignup()">
                  <div class="flex flex-col gap-2 mb-3">
                    <button
                      type="button"
                      matButton="tonal"
                      class="provider-login w-full border border-gray-200 rounded-lg justify-center flex items-center h-[42px]"
                      [disabled]="authLoading()"
                      (click)="joinWithProvider('google')"
                    >
                      <img src="assets/images/google.svg" class="h-4 mr-2" />
                      <span>{{ 'Sessions.Signup.ContinueWithGoogle' | translate }}</span>
                    </button>

                    <button
                      type="button"
                      matButton="tonal"
                      class="provider-login w-full border border-gray-200 rounded-lg justify-center flex items-center h-[42px]"
                      [disabled]="authLoading()"
                      (click)="joinWithProvider('microsoft')"
                    >
                      <img src="assets/images/microsoft.svg" class="h-4 mr-2" />
                      <span>{{ 'Sessions.Signup.ContinueWithMicrosoft' | translate }}</span>
                    </button>
                  </div>

                  <div class="or-divider flex items-center gap-3 my-3">
                    <div class="flex-1 h-px bg-gray-200"></div>
                    <span class="text-[13px] text-gray-400 whitespace-nowrap">{{ 'Sessions.Signup.OrSignUpWithEmail' | translate }}</span>
                    <div class="flex-1 h-px bg-gray-200"></div>
                  </div>

                  <div class="w-full mb-2">
                    <mat-label class="form-label mb-1">{{ 'Sessions.Signin.FullNameField' | translate }}</mat-label>
                    <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
                      <input matInput formControlName="fullName" type="text" autocomplete="name" [placeholder]="'Sessions.Signin.FullNamePlaceholder' | translate" />
                    </mat-form-field>
                  </div>
                  <div class="w-full mb-2">
                    <mat-label class="form-label mb-1">{{ 'Sessions.Signup.EmailField' | translate }}</mat-label>
                    <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
                      <input matInput formControlName="email" type="email" autocomplete="email" [placeholder]="'Sessions.Signup.EmailPlaceholder' | translate" />
                    </mat-form-field>
                  </div>
                  <div class="w-full mb-2">
                    <mat-label class="form-label mb-1">{{ 'Sessions.Signup.PasswordField' | translate }}</mat-label>
                    <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
                      <input
                        matInput
                        formControlName="password"
                        [type]="hidePassword() ? 'password' : 'text'"
                        autocomplete="new-password"
                        [placeholder]="'Sessions.Signup.PasswordPlaceholder' | translate"
                      />
                      <button mat-icon-button matSuffix type="button" (click)="hidePassword.set(!hidePassword())">
                        <mat-icon>{{ hidePassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
                      </button>
                    </mat-form-field>
                  </div>

                  <button class="w-full py-1 text-sm rounded-lg font-medium mt-2" type="submit" [disabled]="authLoading() || signupForm.invalid" mat-flat-button color="primary">
                    {{ 'Sessions.Signup.CreateAccount' | translate }}
                  </button>
                  <div class="flex justify-center mt-3">
                    <span class="text-sm text-gray-500">{{ 'Sessions.Signup.AlreadyHaveAccount' | translate }}&nbsp;</span>
                    <a (click)="authMode.set('login')" class="text-sm font-semibold text-primary hover:text-primary-dark cursor-pointer transition-colors">
                      {{ 'Sessions.Signup.SignIn' | translate }}
                    </a>
                  </div>
                </form>
              }

              @if (authError()) {
                <div class="flex items-center gap-2 bg-red-50 text-red-700 rounded-lg px-3 py-2 mt-4 text-sm animate-fade-in-up">
                  <span>{{ authError() }}</span>
                </div>
              }

              @if (authLoading()) {
                <div class="mt-4">
                  <mat-progress-bar mode="indeterminate" class="rounded-full"></mat-progress-bar>
                </div>
              }

              <app-language-selector class="mt-6 flex justify-center w-full" />

              <!-- Trust footer -->
              <div class="flex items-center justify-center gap-1.5 mt-4 text-gray-400 text-xs">
                <mat-icon class="text-[14px]! h-3.5! w-3.5!">lock</mat-icon>
                <span>{{ 'Sessions.Login.SecureConnection' | translate }}</span>
              </div>
            </div>
          }
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        height: 100vh;
        width: 100%;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #f6f8fc 0%, #eef1f8 100%);
      }

      ::ng-deep .provider-login .mdc-button__label {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
      }

      .card {
        border-radius: 12px;
        width: 100%;
        max-width: 440px;
        padding: 2rem 1.5rem;
        background-color: #fff;
        box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
        border: 1px solid #eaecf0;
        animation: fadeInUp 0.5s ease-out;
      }

      @media (min-width: 640px) {
        .card {
          padding: 2.5rem;
        }
      }

      .or-divider {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin: 0.75rem 0;
      }

      .provider-login {
        width: 100%;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
      }

      .provider-login img {
        height: 16px;
        margin-right: 8px;
      }

      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    TranslateModule,
    LanguageSelectorComponent,
    RouterLink,
  ],
})
export class JoinComponent implements OnInit {
  private aroute = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private auth = inject(Auth);

  token: string | null = null;
  workspaceName = 'SkillzTank';
  logoUrl: string | null = null;
  allowedDomain: string | null = null;

  loading = signal(true);
  error = signal(false);
  joining = signal(false);
  currentUser = signal<any>(null);

  authMode = signal<'login' | 'signup'>('login');
  hidePassword = signal(true);
  authLoading = signal(false);
  authError = signal<string | null>(null);

  private fb = inject(FormBuilder);
  loginForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]],
  });
  signupForm = this.fb.group({
    fullName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
  });

  ngOnInit() {
    this.token = this.aroute.snapshot.paramMap.get('token');

    // Listen to Firebase Auth state
    this.auth.onAuthStateChanged((user) => {
      this.currentUser.set(user);

      // Auto-join if returning from a login flow with autoJoin=true
      if (user && this.aroute.snapshot.queryParamMap.get('autoJoin') === 'true') {
        // Clear the autoJoin param from URL to prevent loop on reload
        this.router.navigate([], { queryParams: { autoJoin: null }, queryParamsHandling: 'merge', replaceUrl: true });

        // Slight delay to ensure workspace details (like allowedDomain) are loaded
        setTimeout(() => {
          if (!this.error() && !this.joining() && this.isEmailDomainValid()) {
            this.acceptJoin();
          }
        }, 500);
      }
    });

    if (!this.token) {
      this.error.set(true);
      this.loading.set(false);
      return;
    }

    this.authService.getJoinLinkInfo(this.token).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success && res.payload) {
          this.workspaceName = res.payload.workspaceName;
          this.logoUrl = res.payload.logoUrl;
          this.allowedDomain = res.payload.allowedDomain;
        } else {
          this.error.set(true);
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      },
    });
  }

  onLogoError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/creatives/logo-w70.png';
  }

  isEmailDomainValid(): boolean {
    if (!this.allowedDomain) return true;
    const email = this.currentUser()?.email || '';
    const domain = (email.split('@')[1] || '').toLowerCase();
    return domain === this.allowedDomain.toLowerCase();
  }

  async joinWithProvider(provider: 'google' | 'microsoft') {
    this.authError.set(null);
    this.authLoading.set(true);
    try {
      const chosenProvider = provider === 'google' ? this.authService.firebaseGoogleProvider : this.authService.firebaseMicrosoftProvider;
      await signInWithPopup(this.auth, chosenProvider);
      this.triggerAutoJoin();
    } catch (err: any) {
      console.error(err);
      this.authError.set(this.translate.instant('Snackbar.Error'));
      this.authLoading.set(false);
    }
  }

  async joinWithLogin() {
    if (this.loginForm.invalid) return;
    this.authError.set(null);
    this.authLoading.set(true);
    try {
      const { email, password } = this.loginForm.value;
      await signInWithEmailAndPassword(this.auth, email!, password!);
      this.triggerAutoJoin();
    } catch (err: any) {
      console.error(err);
      this.authError.set(this.translate.instant('Snackbar.CredentialsErrors'));
      this.authLoading.set(false);
    }
  }

  async joinWithSignup() {
    if (this.signupForm.invalid) return;
    this.authError.set(null);
    this.authLoading.set(true);
    try {
      const { email, password, fullName } = this.signupForm.value;
      const cred = await createUserWithEmailAndPassword(this.auth, email!, password!);
      if (cred.user) {
        await updateProfile(cred.user, { displayName: fullName });
      }
      this.triggerAutoJoin();
    } catch (err: any) {
      console.error(err);
      const code = err?.code || '';
      if (code === 'auth/email-already-in-use') {
        this.authError.set(this.translate.instant('Sessions.Signin.EmailAlreadyInUseError'));
      } else {
        this.authError.set(this.translate.instant('Sessions.Signup.ErrorGeneric'));
      }
      this.authLoading.set(false);
    }
  }

  private triggerAutoJoin() {
    setTimeout(() => {
      if (!this.error() && !this.joining() && this.isEmailDomainValid()) {
        this.acceptJoin();
      }
    }, 500);
  }

  async signOut() {
    await signOut(this.auth);
    this.currentUser.set(null);
  }

  async acceptJoin() {
    if (!this.token) return;
    this.joining.set(true);
    try {
      const res = await firstValueFrom(this.authService.acceptJoinLink(this.token));
      if (res.success) {
        if (res.tenantId) {
          localStorage.setItem('active_workspace', res.tenantId);
        }
        this.toast.snackbar.open(this.translate.instant('Snackbar.JoinSuccess') || 'Successfully joined!', 'Ok', { duration: 3000 });
        window.location.href = '/home';
      }
    } catch (err: any) {
      console.error('Failed to accept join link', err);
      if (err.error?.msg === 'domain-locked' && err.error?.allowedDomain) {
        const domainMsg = this.translate.instant('Snackbar.DomainLockedError', { domain: err.error.allowedDomain });
        this.toast.snackbar.open(domainMsg, 'Ok', { duration: 6000 });
      } else {
        this.toast.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 3000 });
      }
    } finally {
      this.joining.set(false);
    }
  }
}
